// The RMS landing strip (spec: .claude/features/12928-rms-strip-layouts.md).
//
// The server resolves each user's strip: which views, in which group, in what
// order, pinned or not. It arrives in the bootstrap as `strip` and every write
// is one operation whose answer replaces it, so the client never decides an
// order on its own and can never send the server a list it does not hold
// (the bug that kept a staff member's reorders from saving, Slack
// C073TAECDDG/p1791422897037399).

import { useCallback } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import useOrgId from "@/components/FpAppV4/hooks/useOrgId";
import {
  RMS_BOOTSTRAP_QUERY_KEY,
  rmsBootstrapQueryKey,
  type RmsBootstrap,
} from "./bootstrap";
import {
  applyStripOperation,
  DEFAULT_STRIP_COLLECTION,
  moveMembership,
  setStripGroupMembers,
  stripOf,
  type RmsStrip,
  type StripCollection,
  type StripOperation,
} from "./stripModel";

export * from "./stripModel";

// ---- Cache --------------------------------------------------------------

// The RMS strip arrives in the bootstrap; another collection's strip (the
// documents page's, #11422 R6) is read on its own, since that page has no
// bootstrap. Each collection keeps its own cache entry, so the two strips
// never answer for each other.

/** The cache entries of a collection's strip, every organization's. */
export const stripCacheKey = (
  collection: StripCollection
): readonly unknown[] =>
  collection === "rms" ? RMS_BOOTSTRAP_QUERY_KEY : ["rms-strip", collection];

/** A collection's own strip (not the RMS's), for one organization. */
export const collectionStripQueryKey = (
  collection: Exclude<StripCollection, "rms">,
  orgId: string | undefined
) => ["rms-strip", collection, orgId] as const;

/** The strip the cache holds for `collection`, if it has been read. */
export const cachedStrip = (
  queryClient: QueryClient,
  orgId: string | undefined,
  collection: StripCollection
): RmsStrip | undefined => {
  if (collection === "rms") {
    const cached = queryClient.getQueryData<RmsBootstrap>(
      rmsBootstrapQueryKey(orgId)
    );
    return cached ? stripOf(cached) : undefined;
  }
  return queryClient.getQueryData<RmsStrip>(
    collectionStripQueryKey(collection, orgId)
  );
};

/** Write `collection`'s strip for the user's organization. Cancels any read
 * of it in flight first, so an older answer cannot land on top. */
export const setStrip = (
  queryClient: QueryClient,
  orgId: string | undefined,
  collection: StripCollection,
  updater: (strip: RmsStrip) => RmsStrip
): void => {
  void queryClient.cancelQueries({ queryKey: stripCacheKey(collection) });
  if (collection === "rms") {
    queryClient.setQueryData<RmsBootstrap>(
      rmsBootstrapQueryKey(orgId),
      (old) => (old ? { ...old, strip: updater(stripOf(old)) } : old)
    );
    return;
  }
  queryClient.setQueryData<RmsStrip>(
    collectionStripQueryKey(collection, orgId),
    (old) => (old ? updater(old) : old)
  );
};

/** Write the user's strip into their organization's bootstrap. Cancels any
 * bootstrap read in flight first, so an older answer cannot land on top. */
export const setBootstrapStrip = (
  queryClient: QueryClient,
  orgId: string | undefined,
  updater: (strip: RmsStrip) => RmsStrip
): void => setStrip(queryClient, orgId, "rms", updater);

/** Read `collection`'s strip again. */
export const refetchStrip = (
  queryClient: QueryClient,
  collection: StripCollection
): Promise<void> =>
  queryClient.invalidateQueries({ queryKey: stripCacheKey(collection) });

// ---- Requests -----------------------------------------------------------

// Every strip write runs after the one before it finishes, so each answer is
// newer than the last one written into the cache: two quick drags, or the
// several moves of one group edit, can never land out of order.
// Each collection's writes queue on their own: the two strips never wait on
// each other.
const stripWrites = (collection: StripCollection): { id: string } => ({
  id: collection === "rms" ? "rms-strip-writes" : `${collection}-strip-writes`,
});

/**
 * Whether a strip write is still being saved: its answer is the whole strip, so a re-read of the
 * bootstrap landing first would wipe that change off the screen.
 */
export const stripWritesPending = (queryClient: QueryClient): boolean =>
  queryClient
    .getMutationCache()
    .getAll()
    .some(
      (m) =>
        m.options.scope?.id === stripWrites(DEFAULT_STRIP_COLLECTION).id &&
        m.state.status === "pending"
    );

const stripUrl = `${BASE_API_URL_V2}/api/v2/rms/strip`;

/** `url` asking for `collection`. The RMS's is the routes' default, so its
 * requests name none and read as they always have. */
export const withCollection = (
  url: string,
  collection: StripCollection
): string =>
  collection === "rms"
    ? url
    : `${url}${url.includes("?") ? "&" : "?"}collection=${collection}`;

const fetchStrip = async (
  collection: StripCollection,
  signal?: AbortSignal
): Promise<RmsStrip> => {
  const response = await fpFetch(withCollection(stripUrl, collection), {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  });
  if (!response.ok) {
    throw new Error(`Strip request failed: ${response.status}`);
  }
  return response.json();
};

/**
 * A collection's strip, read on its own (the documents page's, #11422 R6):
 * which of the organization's views sit in which group, in what order. The
 * RMS strip is the bootstrap's and is never read here.
 */
export function useCollectionStrip<TData = RmsStrip>(
  collection: Exclude<StripCollection, "rms">,
  options?: { select?: (strip: RmsStrip) => TData; enabled?: boolean }
) {
  const orgId = useOrgId();
  return useQuery<RmsStrip, Error, TData>({
    queryKey: collectionStripQueryKey(collection, orgId),
    queryFn: ({ signal }) => fetchStrip(collection, signal),
    enabled: Boolean(orgId) && (options?.enabled ?? true),
    staleTime: 30 * 1000,
    select: options?.select,
  });
}

const sendStrip = async (
  path: string,
  method: "POST" | "DELETE",
  collection: StripCollection,
  body?: unknown
): Promise<RmsStrip> => {
  const response = await fpFetch(
    withCollection(`${stripUrl}${path}`, collection),
    {
      method,
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    }
  );
  if (!response.ok) {
    throw new Error(`Strip request failed: ${response.status}`);
  }
  return response.json();
};

/**
 * One change to the user's own strip. It shows at once, then the server's
 * answer replaces it. When the request fails the strip goes back to what it
 * was and is read again, so the screen never keeps a change the server did
 * not make.
 */
export const useStripOperation = (
  collection: StripCollection = DEFAULT_STRIP_COLLECTION
) => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();
  const scope = stripWrites(collection);
  // The changes made after `mutationId` whose answers have not landed, in
  // order. Each already shows on screen; an earlier answer must not wipe
  // them, so they go back on top of it. React Query numbers mutations once
  // per client, so the order holds across a remount of the page.
  const waitingAfter = (mutationId: number): StripOperation[] =>
    queryClient
      .getMutationCache()
      .getAll()
      .filter(
        (m) =>
          m.options.scope?.id === scope.id &&
          m.state.status === "pending" &&
          m.mutationId > mutationId
      )
      .sort((a, b) => a.mutationId - b.mutationId)
      .map((m) => m.state.variables as StripOperation);
  // This call's own number, read back in its callbacks.
  const ownId = (operation: StripOperation): number =>
    queryClient
      .getMutationCache()
      .getAll()
      .find((m) => m.state.variables === operation)?.mutationId ?? -1;
  const mutation = useMutation({
    scope,
    mutationFn: (operation: StripOperation) =>
      sendStrip("/operations", "POST", collection, {
        scope: "personal",
        ...operation,
      }),
    onMutate: (operation): StripContext => {
      const previous = cachedStrip(queryClient, orgId, collection);
      setStrip(queryClient, orgId, collection, (strip) =>
        applyStripOperation(strip, operation)
      );
      return { previous };
    },
    onSuccess: (strip, operation) =>
      setStrip(queryClient, orgId, collection, () =>
        waitingAfter(ownId(operation)).reduce(applyStripOperation, strip)
      ),
    onError: (_error, operation, context) => {
      const waiting = waitingAfter(ownId(operation));
      const previous = context?.previous;
      if (previous) {
        setStrip(queryClient, orgId, collection, () =>
          waiting.reduce(applyStripOperation, previous)
        );
      }
      // Read the strip again, unless a later change is still being saved:
      // its answer is the whole strip, and a re-read landing first would
      // wipe that change off the screen.
      if (waiting.length === 0) {
        void refetchStrip(queryClient, collection);
      }
    },
  });
  // Each change is sent as its own object, so `ownId` finds exactly it even
  // when a caller sends the same operation twice.
  const send = mutation.mutate;
  const mutate = useCallback(
    (...[operation, options]: Parameters<typeof send>) =>
      send({ ...operation }, options),
    [send]
  );
  const sendAsync = mutation.mutateAsync;
  const mutateAsync = useCallback(
    (...[operation, options]: Parameters<typeof sendAsync>) =>
      sendAsync({ ...operation }, options),
    [sendAsync]
  );
  return { ...mutation, mutate, mutateAsync };
};

interface StripContext {
  previous: RmsStrip | undefined;
}

/** A change to which views a group holds (#13896): it changes the group for
 * everyone who can see it, so it needs edit on the group (and, to add, on
 * the view) and may be refused. */
export type GroupMembershipChange =
  | { kind: "add"; groupId: number; viewId: number }
  | { kind: "remove"; groupId: number; viewId: number }
  | { kind: "replace"; groupId: number; viewIds: number[] };

const groupViewsUrl = (groupId: number) =>
  `${BASE_API_URL_V2}/api/v2/rms/view-groups/${groupId}/views`;

// The answer is the reader's strip of `collection`, which the request names.
const sendMembership = async (
  change: GroupMembershipChange,
  collection: StripCollection
): Promise<RmsStrip> => {
  const url =
    change.kind === "replace"
      ? groupViewsUrl(change.groupId)
      : `${groupViewsUrl(change.groupId)}/${change.viewId}`;
  const response =
    change.kind === "replace"
      ? await fpFetch(withCollection(url, collection), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ view_ids: change.viewIds }),
        })
      : await fpFetch(withCollection(url, collection), {
          method: change.kind === "add" ? "POST" : "DELETE",
          headers: { "Content-Type": "application/json" },
        });
  if (!response.ok) {
    throw new Error(`Group membership request failed: ${response.status}`);
  }
  return response.json();
};

/** The strip after `change`, as the server will answer it. */
export const applyMembershipChange = (
  strip: RmsStrip,
  change: GroupMembershipChange
): RmsStrip =>
  change.kind === "replace"
    ? setStripGroupMembers(strip, change.groupId, change.viewIds)
    : moveMembership(strip, change.viewId, {
        leave: change.kind === "remove" ? [change.groupId] : [],
        join: change.kind === "add" ? change.groupId : null,
      });

/**
 * Add a view to a group, take one out, or set a group's views. Each answers
 * the reader's strip, which replaces the cache; the change shows at once and
 * goes back, with a re-read, when the server refuses it. It runs in turn with
 * the strip's other writes, so a move made of a removal and an addition lands
 * in order.
 */
export const useGroupMembership = (
  collection: StripCollection = DEFAULT_STRIP_COLLECTION
) => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();
  return useMutation({
    scope: stripWrites(collection),
    mutationFn: (change: GroupMembershipChange) =>
      sendMembership(change, collection),
    onMutate: (change): StripContext => {
      const previous = cachedStrip(queryClient, orgId, collection);
      setStrip(queryClient, orgId, collection, (strip) =>
        applyMembershipChange(strip, change)
      );
      return { previous };
    },
    onSuccess: (strip) => setStrip(queryClient, orgId, collection, () => strip),
    onError: (_error, _change, context) => {
      const previous = context?.previous;
      if (previous) setStrip(queryClient, orgId, collection, () => previous);
      void refetchStrip(queryClient, collection);
    },
  });
};

/** Drop the user's own layout: they follow the org default again. */
export const useResetStrip = (
  collection: StripCollection = DEFAULT_STRIP_COLLECTION
) => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();
  return useMutation({
    scope: stripWrites(collection),
    mutationFn: () => sendStrip("/personal", "DELETE", collection),
    onSuccess: (strip) => setStrip(queryClient, orgId, collection, () => strip),
    onError: () => refetchStrip(queryClient, collection),
  });
};

/** An RMS manager makes their strip the org default. */
export const useSetDefaultFromMine = (
  collection: StripCollection = DEFAULT_STRIP_COLLECTION
) => {
  const queryClient = useQueryClient();
  const orgId = useOrgId();
  return useMutation({
    scope: stripWrites(collection),
    mutationFn: () => sendStrip("/default/from-mine", "POST", collection),
    onSuccess: (strip) => setStrip(queryClient, orgId, collection, () => strip),
    onSettled: () => refetchStrip(queryClient, collection),
  });
};
